/**
 * Revision 7 (R7-B / R7-C): the body of POST /workwise/cost-estimate/, built from
 * the wizard form, and the helpers that present the backend's answer: lines
 * grouped per server, "—" for unpriced lines, the total, and the payload's
 * estimatedMonthlyCost. Nothing is priced here; the prices come from the backend
 * (Azure Retail Prices API).
 *
 * Node-safe: relative imports only, no import.meta (the mock layer uses it).
 */
import { toAzureLocation } from '../../config/azureRegions'
import { DB_ENGINE_VERSION, DISK_SIZE_MAX_GB, DISK_SIZE_MIN_GB, DISK_TYPES } from '../../config/workwise'
import type {
  CostEstimate,
  CostEstimateLine,
  CostEstimateRequest,
  CostItem,
  DiskFormValue,
  PayloadDisk,
  WorkwiseFormValues,
  WorkwiseRole,
} from '../../types/workwise'
import { formatUsd } from './cost'
import { pricedDbDisks } from './dbStorage'

/** The estimate is re-requested this long after the last relevant change. */
export const COST_ESTIMATE_DEBOUNCE_MS = 400
/** The backend caches retail prices for 24 h; an answer for the same inputs is reused for 10 minutes. */
export const COST_ESTIMATE_STALE_MS = 10 * 60_000
/** What the totals show when the estimate endpoint fails (nothing is blocked). */
export const PRICE_UNAVAILABLE = 'Price unavailable'

/** Only complete, valid disks are priced; a disk still being typed is left out until it is valid (app and gateway lists). */
function pricedDisks(disks: readonly DiskFormValue[] | undefined): PayloadDisk[] {
  return (disks ?? []).flatMap((disk) => {
    if (!disk) return []
    const sizeGb = Number(disk.sizeGb)
    const valid = Number.isInteger(sizeGb) && sizeGb >= DISK_SIZE_MIN_GB && sizeGb <= DISK_SIZE_MAX_GB && DISK_TYPES.includes(disk.type)
    return valid ? [{ sizeGb, type: disk.type }] : []
  })
}

function sharedServerId(id: number | undefined): { sharedServerId?: number } {
  return id != null && Number.isInteger(Number(id)) ? { sharedServerId: Number(id) } : {}
}

/**
 * The inputs that change the price, in the backend's camelCase shape; null while
 * there is nothing to price (no region or size yet). Its JSON is the query key, so
 * typing a customer name never re-requests the estimate.
 */
export function costEstimateRequest(values: WorkwiseFormValues): CostEstimateRequest | null {
  const region = toAzureLocation(values?.region ?? '')
  const app = values?.appServer
  if (!region || !app?.sku) return null
  const dedicated = values.deploymentModel === 'dedicated'
  const db = values.dbServer
  const gateway = values.gatewayServer
  return {
    region,
    appServer: { sku: app.sku, os: app.os, osDiskType: app.osDiskType, disks: pricedDisks(app.disks) },
    dbServer: dedicated
      ? { mode: 'dedicated', engineVersion: DB_ENGINE_VERSION, disks: pricedDbDisks(db?.storage) }
      : { mode: 'shared', ...sharedServerId(db?.sharedServerId) },
    gatewayServer: dedicated
      ? { mode: 'dedicated', publicIp: Boolean(gateway?.publicIp), disks: pricedDisks(gateway?.disks) }
      : { mode: 'shared', ...sharedServerId(gateway?.sharedServerId) },
  }
}

// ─── Presentation ────────────────────────────────────────────────────────────

export const COST_ROLE_LABELS: Record<WorkwiseRole, string> = {
  app: 'App Server',
  db: 'Database Server',
  gateway: 'Gateway Server',
}

const ROLE_ORDER: WorkwiseRole[] = ['app', 'db', 'gateway']
/** Within a server: compute, SQL licence, OS disk, data disks, public IP, shared share. */
const ITEM_ORDER: CostItem[] = ['compute', 'sql_license', 'os_disk', 'data_disk', 'public_ip', 'shared']

export interface CostGroup {
  role: string
  title: string
  lines: CostEstimateLine[]
  /** Sum of the priced lines of this server. */
  subtotal: number
  priced: number
  unpriced: number
}

const round2 = (n: number) => Math.round(n * 100) / 100

export function isPricedLine(line: Pick<CostEstimateLine, 'monthly'>): boolean {
  return typeof line.monthly === 'number' && Number.isFinite(line.monthly)
}

function rank(order: readonly string[], value: string): number {
  const index = order.indexOf(value)
  return index === -1 ? order.length : index
}

/** The lines per server (App, Database, Gateway; any other role after them), each in item order. */
export function groupCostLines(lines: readonly CostEstimateLine[] | undefined): CostGroup[] {
  const all = lines ?? []
  const roles = [...new Set(all.map((l) => l.role as string))].sort((a, b) => rank(ROLE_ORDER, a) - rank(ROLE_ORDER, b))
  return roles.map((role) => {
    const own = all
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => line.role === role)
      .sort((a, b) => rank(ITEM_ORDER, a.line.item) - rank(ITEM_ORDER, b.line.item) || a.index - b.index)
      .map(({ line }) => line)
    const priced = own.filter(isPricedLine)
    return {
      role,
      title: COST_ROLE_LABELS[role as WorkwiseRole] ?? role,
      lines: own,
      subtotal: round2(priced.reduce((sum, l) => sum + (l.monthly ?? 0), 0)),
      priced: priced.length,
      unpriced: own.length - priced.length,
    }
  })
}

/** "$19.71", or "—" for an unpriced line. */
export function lineAmountText(line: Pick<CostEstimateLine, 'monthly'>): string {
  return isPricedLine(line) ? formatUsd(line.monthly as number) : '—'
}

/**
 * A total as shown: "—" when nothing is priced (never "$0.00"), otherwise the
 * amount, with "+ unpriced" when some lines have no price.
 */
export function formatCostAmount(amount: number, priced: number, unpriced: number, suffix = ''): string {
  if (priced === 0) return '—'
  return `${formatUsd(amount)}${suffix}${unpriced > 0 ? ' + unpriced' : ''}`
}

/** The estimate's total: total_monthly, "+ unpriced" when unpriced_count > 0. */
export function formatCostTotal(estimate: Pick<CostEstimate, 'lines' | 'total_monthly' | 'unpriced_count'>, suffix = ''): string {
  const priced = (estimate.lines ?? []).filter(isPricedLine).length
  return formatCostAmount(estimate.total_monthly, priced, estimate.unpriced_count ?? 0, suffix)
}

/** The payload's estimatedMonthlyCost: the estimate's total_monthly, 0 when there is no usable estimate. */
export function estimatedMonthlyCostOf(estimate: Pick<CostEstimate, 'total_monthly'> | null | undefined): number {
  const total = Number(estimate?.total_monthly)
  return Number.isFinite(total) && total > 0 ? round2(total) : 0
}

/** The estimate as a screen shows it (see hooks/useWorkwise useCostEstimate). */
export interface CostEstimateState {
  /** idle: nothing to price yet; loading: first answer pending; ready: `estimate` is set; error: "Price unavailable". */
  status: 'idle' | 'loading' | 'ready' | 'error'
  estimate?: CostEstimate
  /** True while newer inputs are being priced; `estimate` is then the previous answer. */
  updating: boolean
}

/** The total as text: the amount, "Calculating…" before the first answer, "Price unavailable", or "—". */
export function costTotalText(state: CostEstimateState, suffix = ''): string {
  if (state.status === 'error') return PRICE_UNAVAILABLE
  if (state.status === 'loading') return 'Calculating…'
  return state.estimate ? formatCostTotal(state.estimate, suffix) : '—'
}

/** The backend's notes and the source as one small-print paragraph. */
export function costNotesText(estimate: Pick<CostEstimate, 'notes' | 'source' | 'priced_at'>): string {
  const sentences = [...(estimate.notes ?? []), costSourceText(estimate)]
    .map((s) => s.trim().replace(/\.+$/, ''))
    .filter(Boolean)
  return sentences.length ? `${sentences.join('. ')}.` : ''
}

/** "Source: Azure Retail Prices API (pay-as-you-go list prices), priced 20 Oct 2026" for the small source note. */
export function costSourceText(estimate: Pick<CostEstimate, 'source' | 'priced_at'>): string {
  const source = `Source: ${(estimate.source || 'Azure retail prices').trim()}`
  const when = estimate.priced_at ? new Date(estimate.priced_at) : null
  if (!when || Number.isNaN(when.getTime())) return source
  return `${source}, priced ${when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}`
}
